import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

export interface CoachSuggestion {
  id: string;
  suggestion_type: string;
  message: string;
  cta_label: string | null;
  cta_url: string | null;
  priority: number;
  node_id: string | null;
  course_id: string | null;
}

interface CoachSuggestionCardProps {
  suggestion: CoachSuggestion;
  onDismiss: (id: string) => void;
  onAct?: (id: string) => void;
  compact?: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  test_out: "Ready to test",
  try_tutor: "Try AI tutor",
  at_risk_node: "Key skill",
  prerequisite_gap: "Prerequisite",
  modality_switch: "Try slides",
  objective_coverage_gap: "Coverage",
  pace_warning: "Pace",
  remediation: "Extra help",
};

export function CoachSuggestionCard({ suggestion, onDismiss, onAct, compact = false }: CoachSuggestionCardProps) {
  const typeLabel = TYPE_LABELS[suggestion.suggestion_type] ?? suggestion.suggestion_type;
  const isExternal = suggestion.cta_url?.startsWith("http");

  const content = (
    <div
      className={`rounded-xl border bg-card p-4 ${compact ? "py-3" : ""}`}
      style={{
        borderColor: "rgba(47,97,71,0.25)",
        background: "linear-gradient(135deg, rgba(47,97,71,0.06) 0%, rgba(234,230,220,0.04) 100%)",
      }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary/80">{typeLabel}</span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onDismiss(suggestion.id);
              }}
              className="shrink-0 p-1 -m-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-sm text-foreground mt-1 leading-snug">{suggestion.message}</p>
          {suggestion.cta_label && suggestion.cta_url && (
            <div className="mt-2">
              {isExternal ? (
                <a
                  href={suggestion.cta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                >
                  {suggestion.cta_label} →
                </a>
              ) : (
                <Link
                  to={suggestion.cta_url}
                  className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                >
                  {suggestion.cta_label} →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (suggestion.cta_url && !isExternal) {
    return (
      <Link
        to={suggestion.cta_url}
        className="block"
        onClick={() => onAct?.(suggestion.id)}
      >
        {content}
      </Link>
    );
  }

  return content;
}

export default CoachSuggestionCard;
